import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
} from '@mui/material';
import useAutoFocus from '../../hooks/useAutoFocus';

/**
 * ProjectDangerDialog - a reusable, project-name-typed two-step confirmation for
 * destructive project actions (GH #195). Replaces the native `window.confirm()`
 * previously used for Archive: the confirm button stays disabled until the caller
 * types the project's exact name, which is a stronger "double confirmation" than a
 * second click and matches the pattern used for other typed/reason-based
 * destructive dialogs in this codebase (see `DeleteDialog`).
 *
 * Two variants:
 * - `archive` (soft): flips the project to `archived`; data is kept, just hidden
 *   from active lists. Used by both the org project list and the project's own
 *   "Danger zone".
 * - `hardDelete` (super-admin only, rendered only when the caller is one): a
 *   genuinely irreversible purge of the project and everything under it. Kept
 *   visually distinct (red filled button + an explicit warning Alert) so it can't
 *   be mistaken for Archive.
 *
 * @component
 */
const VARIANTS = {
  archive: {
    title: 'Archive project?',
    confirmLabel: 'Yes, archive this project',
    buttonVariant: 'contained',
    body: (name) =>
      `Archiving "${name}" removes it from active project lists. Its requirements ` +
      'are kept, not deleted — but there is currently no UI to bring it back, so ' +
      'treat this as final from here.',
  },
  hardDelete: {
    title: 'Permanently delete this project?',
    confirmLabel: 'Yes, permanently delete',
    buttonVariant: 'contained',
    body: (name) =>
      `This PERMANENTLY deletes "${name}" and every requirement, link, and history ` +
      'record it contains. This is a super-admin hard delete, not an archive — ' +
      'there is no undo and no recovery from this UI.',
  },
};

const ProjectDangerDialog = ({ open, onClose, onConfirm, projectName, variant = 'archive' }) => {
  const [typed, setTyped] = useState('');
  // #793: rendered unconditionally by every caller with `open` toggling — a bare
  // `autoFocus` only fires on the FIRST mount, so a reopen after the first
  // silently lost focus. Focuses the typed-confirmation field, never the
  // destructive button (see hooks/useAutoFocus's own NOT-for-a-destructive-
  // button note).
  const focusRef = useAutoFocus(open);

  // Reset the typed confirmation each time the dialog opens.
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const cfg = VARIANTS[variant] || VARIANTS.archive;
  const expected = (projectName || '').trim();
  const matches = expected.length > 0 && typed.trim() === expected;
  const titleId = `project-danger-dialog-title-${variant}`;
  const descId = `project-danger-dialog-description-${variant}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      aria-describedby={descId}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id={titleId}>{cfg.title}</DialogTitle>
      <DialogContent>
        <DialogContentText id={descId} sx={{ mb: 2 }}>
          {cfg.body(projectName)}
        </DialogContentText>
        {variant === 'hardDelete' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            This is different from Archive above — it is permanent and cannot be undone.
          </Alert>
        )}
        <TextField
          inputRef={focusRef}
          fullWidth
          size="small"
          label={`Type "${projectName || ''}" to confirm`}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm()}
          color="error"
          variant={cfg.buttonVariant}
          disabled={!matches}
        >
          {cfg.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ProjectDangerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  projectName: PropTypes.string,
  variant: PropTypes.oneOf(['archive', 'hardDelete']),
};

export default ProjectDangerDialog;
